import axios from 'axios';
import {useEffect, useReducer} from 'react';
import {
  Grid,
  Loader,
  Header,
  Segment,
  Popup,
  Input,
  Menu,
  Dropdown,
  Button,
} from 'semantic-ui-react';

import {typeLabel} from 'components/Common/siteTypesLabels';
import httpClient from 'httpClient';

import SiteListTable from './SiteListTable';

import styles from './SiteList.module.scss';

const ownOrManageOptions = [
  {
    text: 'All sites',
    value: 'All',
  },
  {
    text: 'Sites I own',
    value: true,
  },
  {
    text: 'Sites I manage',
    value: false,
  },
];

// Checks if the site type matches the selected type
function typeMatch(typeFilter, type) {
  return typeFilter === 'All' || typeFilter === typeLabel[type];
}

// Checks if the site isOwner flag matches the selected option (`Sites I own` or `Sites I manage`)
function ownershipMatch(ownershipFilter, isOwner) {
  return ownershipFilter === 'All' || ownershipFilter === isOwner;
}

// Checks if the name matches the query
function searchMatch(query, name) {
  return name.includes(query);
}

// Checks if all filters match
function allFiltersMatch(site, typeFilter, ownershipFilter, searchFilter) {
  return (
    typeMatch(typeFilter, site.type) &&
    ownershipMatch(ownershipFilter, site.isOwner) &&
    searchMatch(searchFilter, site.name)
  );
}

// Compares two sites by name, case insensitive
function compareByName(a, b) {
  return a.name.localeCompare(b.name, undefined, {sensitivity: 'base'});
}

// Sorts the list of sites by name in the specified order
function sortSites(list, order) {
  const sorted = [...list].sort(compareByName);
  return order === 'desc' ? sorted.reverse() : sorted;
}

function sitesReducer(state, action) {
  switch (action.type) {
    // dispatched by a successful completion of a call to a backend endpoint to get sites
    case 'SET_DATA': {
      const mergedSites = [...state.sitesList, ...action.data.sitesList];
      const sortedSites = sortSites(mergedSites, state.sortOrder);
      const filteredSites = sortedSites.filter(site =>
        allFiltersMatch(site, state.typeFilter, state.ownershipFilter, state.query)
      );
      return {
        ...state,
        loaded: {...state.loaded, ...action.data.loaded},
        sitesList: sortedSites,
        filteredList: filteredSites,
      };
    }
    // dispatched by a change in the Ownership menu
    case 'FILTER_OWNERSHIP': {
      const filtered = state.sitesList.filter(site =>
        allFiltersMatch(site, state.typeFilter, action.data.ownershipFilter, state.query)
      );
      return {
        ...state,
        ownershipFilter: action.data.ownershipFilter,
        filteredList: filtered,
      };
    }
    // dispatched by a change in the type dropdown
    case 'FILTER_TYPE': {
      const filtered = state.sitesList.filter(site =>
        allFiltersMatch(site, action.data.typeFilter, state.ownershipFilter, state.query)
      );
      return {
        ...state,
        typeFilter: action.data.typeFilter,
        filteredList: filtered,
      };
    }
    // dispatched by a change in the search input
    case 'SEARCH_NAME': {
      const filtered = state.sitesList.filter(site =>
        allFiltersMatch(site, state.typeFilter, state.ownershipFilter, action.data.query)
      );
      return {
        ...state,
        query: action.data.query,
        filteredList: filtered,
      };
    }
    // dispatched by a change in the sort order
    case 'SORT_ORDER': {
      const newOrder = action.data.sortOrder;
      return {
        ...state,
        sortOrder: newOrder,
        sitesList: sortSites(state.sitesList, newOrder),
        filteredList: sortSites(state.filteredList, newOrder),
      };
    }
    case 'SET_ERROR':
      return {
        ...state,
        loaded: {...state.loaded, ...action.data.loaded},
      };
    default:
      throw new Error();
  }
}

const SiteList = () => {
  const initialState = {
    // keep trace of when each individual `my-sites` endpoint returns
    loaded: {
      webeos: false,
      paas: false,
      drupal: false,
      appCatalogue: false,
    },
    // the full sitesList to which the user has admin rights
    sitesList: [],
    // the view over the sitesList (when filters or search are applied)
    filteredList: [],
    // values of the filters:
    ownershipFilter: 'All',
    typeFilter: 'All',
    query: '',
    sortOrder: 'asc',
  };

  const [
    {loaded, sitesList, filteredList, ownershipFilter, typeFilter, query, sortOrder},
    dispatch,
  ] = useReducer(sitesReducer, initialState);

  async function getSites(url, loadedType, cancelToken) {
    try {
      const {
        data: {projects},
      } = await httpClient.get(url, {cancelToken});

      dispatch({
        type: 'SET_DATA',
        data: {sitesList: projects, loaded: {[loadedType]: true}},
      });
    } catch (err) {
      if (!axios.isCancel(err)) {
        dispatch({
          type: 'SET_ERROR',
          data: {loaded: {[loadedType]: true}},
        });
      }
    }
  }

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();

    getSites('/api/webeos/my-sites', 'webeos', cancelToken.token);
    getSites('/api/paas/my-sites', 'paas', cancelToken.token);
    getSites('/api/drupal/my-sites', 'drupal', cancelToken.token);
    getSites('/api/app-catalogue/my-sites', 'appCatalogue', cancelToken.token);

    return () => {
      cancelToken.cancel('Cancelled due to the SiteList component being unmounted.');
    };
  }, []);

  // Checks if all calls to get sites from various sources (clusters and WSDB) completed
  function allLoaded() {
    return Object.values(loaded).every(Boolean);
  }

  return (
    <Grid centered columns={2}>
      <Grid.Row>
        <Grid.Column>
          <Header as="h3" textAlign="center">
            My sites
          </Header>
          {sitesList.length === 0 && allLoaded() && (
            <div style={{textAlign: 'center', paddingTop: 20}}>
              This list is empty since you do not own or moderate any sites.
            </div>
          )}
        </Grid.Column>
      </Grid.Row>

      {sitesList.length > 0 && (
        <Grid.Row className={styles.SiteList}>
          <Grid.Column width={5}>
            <Menu vertical fluid>
              {ownOrManageOptions.map(option => (
                <Menu.Item
                  key={option.text}
                  name={option.text}
                  active={ownershipFilter === option.value}
                  onClick={() => {
                    dispatch({
                      type: 'FILTER_OWNERSHIP',
                      data: {ownershipFilter: option.value},
                    });
                  }}
                >
                  {option.text}
                </Menu.Item>
              ))}
            </Menu>

            <div style={{marginTop: '0.5em'}}>
              <Header as="h5" textAlign="left" style={{marginBottom: '0.5em'}}>
                Sort
              </Header>
              <Popup
                trigger={
                  <Button.Group basic compact style={{width: '100%'}}>
                    <Button
                      aria-label="Sort A → Z"
                      icon="sort alphabet down"
                      active={sortOrder === 'asc'}
                      onClick={() => dispatch({type: 'SORT_ORDER', data: {sortOrder: 'asc'}})}
                    />
                    <Button
                      aria-label="Sort Z → A"
                      icon="sort alphabet up"
                      active={sortOrder === 'desc'}
                      onClick={() => dispatch({type: 'SORT_ORDER', data: {sortOrder: 'desc'}})}
                    />
                  </Button.Group>
                }
                position="left center"
              >
                <Popup.Content>Sort sites by name</Popup.Content>
              </Popup>
            </div>

            <div style={{marginTop: '1em'}}>
              <Header as="h5" textAlign="left" style={{marginBottom: '0.5em'}}>
                Filter
              </Header>
              <Popup
                trigger={
                  <Dropdown
                    selection
                    options={[
                      {text: 'All', value: 'All'},
                      ...[...new Set(Object.values(typeLabel))].map(i => ({text: i, value: i})),
                    ]}
                    fluid
                    value={typeFilter}
                    onChange={(_evt, data) => {
                      dispatch({
                        type: 'FILTER_TYPE',
                        data: {typeFilter: data.value},
                      });
                    }}
                  />
                }
                position="left center"
              >
                <Popup.Content>Filter sites by type</Popup.Content>
              </Popup>
            </div>
          </Grid.Column>

          <Grid.Column width={11}>
            <Popup
              trigger={
                <Input
                  aria-label="Search by name"
                  icon="search"
                  placeholder="Search..."
                  fluid
                  onChange={(_evt, data) => {
                    dispatch({type: 'SEARCH_NAME', data: {query: data.value}});
                  }}
                  value={query}
                />
              }
              position="top center"
            >
              <Popup.Content>Search for sites by name. Search is not case sensitive.</Popup.Content>
            </Popup>
            <SiteListTable filteredList={filteredList} />
          </Grid.Column>
        </Grid.Row>
      )}
      {!allLoaded() && (
        <Grid.Row>
          <Grid.Column>
            <Segment padded="very" basic>
              <Loader active>Loading</Loader>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

export default SiteList;
